import type { DeskDetail } from '@deskworks/shared-types';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { Activity, Bot, BrainCircuit, Check, ChevronLeft, Copy, FolderInput, FolderTree, Gauge, GraduationCap, History, LayoutDashboard, ListTodo, MessagesSquare, MoreHorizontal, PanelsTopLeft, Pause, Pencil, Play, Plus, ScrollText, Share2, SlidersHorizontal, Sparkles, Store, Zap } from 'lucide-react';
import { useState } from 'react';
import { ActivityFeed } from '@/components/activity-feed';
import { JsonBlock } from '@/components/code-block';
import { CreateWorkItemDialog } from '@/components/create-work-item-dialog';
import { StartConversationDialog } from '@/components/conversations';
import { AgentModeToggle } from '@/components/desk/agent-mode-toggle';
import { DESK_STAGE_INFO, StageBadge, StageToggle, TrainingNote } from '@/components/desk/stage-toggle';
import { DeskChat } from '@/components/desk/desk-agent-panel';
import { DeskAgentsSection } from '@/components/desk/desk-agents';
import { DeskConnectionsSection } from '@/components/desk/desk-connections';
import { DeskFiles } from '@/components/desk/desk-files';
import { DeskPulsePanel } from '@/components/desk-card';
import { DeskSecretsSection } from '@/components/desk/desk-secrets';
import { DeskNeedsYou, DeskPermissions } from '@/components/actions/actions';
import { DeskGoalsSection } from '@/components/desk/desk-goals';
import { DeskQuestionsSection } from '@/components/chat/desk-talk';
import { deskIcon, deskTone } from '@/components/desk/desk-look';
import { DeskMetricsSection } from '@/components/desk/desk-metrics';
import { DeskCycleTimes } from '@/components/desk/desk-cycle-times';
import { PulseBadge } from '@/components/desk/pulse';
import { DeskPlaybookSection } from '@/components/desk/desk-playbook';
import { DeskLeadSection } from '@/components/desk/desk-lead';
import { DeskLearningsSection } from '@/components/desk/desk-learnings';
import { AutonomyFigure, DeskTraining, FirstTrainingCard, useTraining } from '@/components/desk/desk-training';
import { DeskPlaybooksSection } from '@/components/desk/desk-playbooks';
import { DeskTriggersSection } from '@/components/desk/desk-triggers';
import { DeskResourcesSection } from '@/components/resources/resources';
import { EditDeskDialog } from '@/components/desk-dialogs';
import { SaveToDeskStoreDialog } from '@/components/desk-store-dialogs';
import { MoveDeskDialog } from '@/components/desk-move-dialog';
import { CloneDeskDialog } from '@/components/desk-clone-dialog';
import { ShareDeskDialog } from '@/components/share-desk-dialog';
import { DefinitionList, MetaItem, Page, PageHeader } from '@/components/page';
import { RelativeTime } from '@/components/relative-time';
import { Section } from '@/components/section';
import { DeskStatusBadge, ExecutionStatusBadge } from '@/components/status-badge';
import { EmptyState, ErrorState, LoadingPage, LoadingRows } from '@/components/states';
import { TechDetails } from '@/components/tech-details';
import { SelectableWorkItemList } from '@/components/work-item-bulk';
import { viewKeys } from '@/components/views/view-host';
import { NewViewDialog, ViewCard } from '@/routes/views';
import type { DeskTab } from '@/router';
import { AgentConversation } from '@/components/work-item/agent-session';
import { AgentSteps } from '@/components/agent-steps';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MobileTabs, MobileTabsSpacer } from '@/components/ui/mobile-tabs';
import { api } from '@/lib/api';
import { useApiMutation, useIsPhone } from '@/lib/hooks';
import { inSentence, t, tr } from '@/lib/i18n';
import { WORK_ITEM_STATUS } from '@/lib/labels';
import { qk } from '@/lib/query-keys';
import { useToast } from '@/lib/toast';
import { cn } from '@/lib/utils';

export function DeskDetailPage() {
  const { workspaceId, deskId } = useParams({ from: '/_authed/w/$workspaceId/desks/$deskId' });
  const desk = useQuery({ queryKey: qk.desk(deskId), queryFn: () => api.desks.get(deskId) });

  if (desk.isPending) return <LoadingPage />;
  if (desk.isError) {
    return (
      <Page>
        <BackLink workspaceId={workspaceId} />
        <ErrorState error={desk.error} onRetry={() => void desk.refetch()} />
      </Page>
    );
  }
  return <DeskView desk={desk.data} />;
}

function DeskTile({ desk }: { desk: DeskDetail }) {
  const Icon = deskIcon(desk);
  return (
    <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-lg', deskTone(desk.id).tile)}>
      <Icon className="size-5" />
    </span>
  );
}

function BackLink({ workspaceId }: { workspaceId: string }) {
  return (
    <Link
      to="/w/$workspaceId/desks"
      params={{ workspaceId }}
      className="mb-4 inline-flex items-center gap-1 text-[13px] text-muted-foreground hover:text-foreground"
    >
      <ChevronLeft className="size-4" /> {t('All desks')}
    </Link>
  );
}

type MenuDialog = 'create' | 'edit' | 'store' | 'move' | 'clone' | 'share';

function DeskView({ desk }: { desk: DeskDetail }) {
  const toast = useToast();
  const navigate = useNavigate();
  const { tab = 'overview' } = useSearch({ from: '/_authed/w/$workspaceId/desks/$deskId' });
  const [menuDialog, setMenuDialog] = useState<MenuDialog | null>(null);
  const workspaceId = desk.workspaceId;
  const invalidate = [qk.desk(desk.id), qk.desks(workspaceId)];
  const paused = desk.status === 'paused';

  const pause = useApiMutation(() => api.desks.pause(desk.id), { invalidate, success: t('Desk paused') });
  const resume = useApiMutation(() => api.desks.resume(desk.id), { invalidate, success: t('Desk is running again') });
  const trigger = useApiMutation(() => api.desks.trigger(desk.id, 'Run manually from the app'), {
    invalidate: [qk.desk(desk.id), qk.activityAll(workspaceId)],
    onSuccess: () => toast.success(t('Desk triggered'), t('The Lead Agent is now checking what needs to be done.')),
  });
  const dialog = (d: MenuDialog) => ({ open: menuDialog === d, onOpenChange: (o: boolean) => setMenuDialog(o ? d : null) });
  // On a phone the full header (pulse, assignment, facts) belongs to the overview; the other tabs start right away.
  const compact = useIsPhone() && tab !== 'overview';

  return (
    <Page wide>
      <BackLink workspaceId={workspaceId} />
      <PageHeader
        actionsOnTop
        title={
          <span className="inline-flex flex-wrap items-center gap-3">
            <DeskTile desk={desk} />
            {desk.name}
            {desk.pulse && desk.status !== 'archived' ? <PulseBadge state={desk.pulse.state} /> : <DeskStatusBadge status={desk.status} />}
            <StageBadge stage={desk.stage} />
          </span>
        }
        banner={desk.pulse && desk.status !== 'archived' && !compact ? <DeskPulsePanel desk={desk} className="lg:w-[calc((100%-3rem)*2/3+1.5rem)]" /> : undefined}
        description={compact ? undefined : desk.assignment || t('No assignment yet. Open “⋯ → Edit desk” to describe what this desk is responsible for.')}
        meta={
          compact ? undefined : <>
            <MetaItem label={t('Lead Agent:')}>
              {desk.leadAgent ? (
                <Link
                  to="/w/$workspaceId/agents/$agentId"
                  params={{ workspaceId, agentId: desk.leadAgent.agentId }}
                  className="hover:text-primary hover:underline"
                >
                  {desk.leadAgent.name}
                </Link>
              ) : (
                <span className="text-amber-700 dark:text-amber-400">{t('not set')}</span>
              )}
            </MetaItem>
            <MetaItem label={t('Open:')}>{desk.openWorkItems}</MetaItem>
          </>
        }
        actions={
          <>
            {/* On a phone outside the overview the header keeps only the menu. */}
            {!compact && (
              <>
                <Button size="sm" variant="outline" onClick={() => setMenuDialog('share')} title={t('A link for people outside your account: the desk’s views and chats with its Lead.')}>
                  <Share2 /> {t('Share desk')}
                </Button>
                <StartConversationDialog desk={desk}>
                  <Button size="sm" variant="secondary" className="border border-primary/30 bg-primary/10 text-primary hover:bg-primary/15">
                    <MessagesSquare /> {t('Talk to agent')}
                  </Button>
                </StartConversationDialog>
                <Button
                  size="sm"
                  onClick={() => trigger.mutate()}
                  loading={trigger.isPending}
                  disabled={paused || desk.status === 'archived'}
                  title={paused ? t('This desk is paused. Resume it first.') : t('Wakes up the Lead Agent so it looks for work.')}
                >
                  <Zap /> {t('Run now')}
                </Button>
              </>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon-sm" aria-label={t('More actions')} title={t('More actions')}>
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {compact && (
                  <DropdownMenuItem onSelect={() => trigger.mutate()} disabled={paused || desk.status === 'archived' || trigger.isPending}>
                    <Zap /> {t('Run now')}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onSelect={() => setMenuDialog('create')}>
                  <Plus /> {t('Create {item}', { item: inSentence(desk.workItemLabel.singular) })}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => setMenuDialog('edit')}>
                  <Pencil /> {t('Edit desk')}
                </DropdownMenuItem>
                {compact && (
                  <DropdownMenuItem onSelect={() => setMenuDialog('share')}>
                    <Share2 /> {t('Share desk')}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem asChild title={t('Describe what should change; the setup assistant drafts it for you to apply.')}>
                  <Link to="/w/$workspaceId/start" params={{ workspaceId }} search={{ desk: desk.id }}>
                    <Sparkles /> {t('Change with VICKI')}
                  </Link>
                </DropdownMenuItem>
                {paused ? (
                  <DropdownMenuItem onSelect={() => resume.mutate()} disabled={resume.isPending}>
                    <Play /> {t('Resume desk')}
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onSelect={() => pause.mutate()} disabled={pause.isPending || desk.status === 'archived'}>
                    <Pause /> {t('Pause desk')}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => setMenuDialog('store')} title={t("Save this desk's setup (no data) so any workspace of your account can install it.")}>
                  <Store /> {t('Save to Desk Store')}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setMenuDialog('clone')} title={t('A copy of this desk in this workspace, e.g. for another language; its Lead adapts it to what you say.')}>
                  <Copy /> {t('Clone desk')}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setMenuDialog('move')} title={t('Move this desk with everything on it into another workspace of your account.')}>
                  <FolderInput /> {t('Move to another workspace')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
      />
      <CreateWorkItemDialog workspaceId={workspaceId} deskId={desk.id} {...dialog('create')} />
      <EditDeskDialog desk={desk} {...dialog('edit')} />
      <SaveToDeskStoreDialog desk={desk} {...dialog('store')} />
      <MoveDeskDialog desk={desk} {...dialog('move')} />
      <CloneDeskDialog desk={desk} {...dialog('clone')} />
      <ShareDeskDialog desk={desk} {...dialog('share')} />

      {paused && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
          {t('This desk is paused. Triggers won’t wake it up, and no new work starts until you resume it.')}
        </div>
      )}

      <Tabs
        value={tab}
        onValueChange={(v) => {
          void navigate({ to: '.', search: (s) => ({ ...s, tab: v === 'overview' ? undefined : (v as DeskTab) }), replace: true });
          // A tab from the bar at the bottom of a phone starts at its top, like a native app's.
          if (window.matchMedia('(max-width: 767px)').matches) document.getElementById('main-scroll')?.scrollTo({ top: 0 });
        }}
      >
        <TabsList className="max-md:hidden">
          <TabsTrigger value="overview">{t('Overview')}</TabsTrigger>
          <TabsTrigger value="work">
            {t('Work')}
            {desk.openWorkItems > 0 && <span className="text-xs text-muted-foreground tabular-nums">{desk.openWorkItems}</span>}
          </TabsTrigger>
          <TabsTrigger value="chat">{t('Chats')}</TabsTrigger>
          <TabsTrigger value="files">{t('Files')}</TabsTrigger>
          <TabsTrigger value="training">
            {t('Training')}
            {desk.trainingConversationId && <span className="size-1.5 rounded-full bg-sky-500" title={t('A training is running')} />}
          </TabsTrigger>
          <TabsTrigger value="setup">
            {t('Setup')}
            {!desk.leadAgent && <span className="size-1.5 rounded-full bg-amber-500" title={t('The desk has no Lead Agent yet')} />}
          </TabsTrigger>
          <TabsTrigger value="history">{t('History')}</TabsTrigger>
        </TabsList>
        <MobileTabs
          label={t('Desk')}
          tabs={[
            { value: 'overview', label: t('Overview'), icon: <LayoutDashboard /> },
            { value: 'work', label: t('Work'), icon: <ListTodo />, badge: desk.openWorkItems },
            { value: 'chat', label: t('Chats'), icon: <MessagesSquare /> },
            { value: 'files', label: t('Files'), icon: <FolderTree /> },
            { value: 'training', label: t('Training'), icon: <GraduationCap />, dot: desk.trainingConversationId ? 'sky' : undefined },
            { value: 'setup', label: t('Setup'), icon: <SlidersHorizontal />, dot: desk.leadAgent ? undefined : 'amber' },
            { value: 'history', label: t('History'), icon: <History /> },
          ]}
        />
        <TabsContent value="overview">
          <DeskOverview desk={desk} />
        </TabsContent>
        <TabsContent value="work">
          <DeskWork desk={desk} />
        </TabsContent>
        <TabsContent value="chat">
          <DeskChat desk={desk} />
        </TabsContent>
        <TabsContent value="files">
          <DeskFiles desk={desk} />
        </TabsContent>
        <TabsContent value="training">
          <DeskTraining desk={desk} />
        </TabsContent>
        <TabsContent value="setup">
          <DeskSetup desk={desk} />
        </TabsContent>
        <TabsContent value="history">
          <DeskHistory desk={desk} />
        </TabsContent>
        <MobileTabsSpacer />
      </Tabs>
    </Page>
  );
}

/** What a person looks at day to day: what needs them, what the Lead does and how the desk is doing. */
function DeskOverview({ desk }: { desk: DeskDetail }) {
  const workspaceId = desk.workspaceId;
  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <div className="flex min-w-0 flex-col gap-6 lg:col-span-2">
        <FirstTrainingCard desk={desk} />
        <DeskNeedsYou workspaceId={workspaceId} deskId={desk.id} />
        <LeadEvaluationCard desk={desk} />
        <DeskViews desk={desk} />
        <DeskMetricsSection desk={desk} />
        <DeskCycleTimes desk={desk} />
      </div>
      <div className="flex min-w-0 flex-col gap-6">
        <DeskAutonomy desk={desk} />
        <DeskQuestionsSection deskId={desk.id} workspaceId={workspaceId} />
        <DeskGoalsSection desk={desk} />
      </div>
    </div>
  );
}

/**
 * How much the desk still needs people (docs/features/desk-training.md#autonomy): interventions per piece of work and
 * where it stands on its way to the next stage — the training is where it gets better.
 */
function DeskAutonomy({ desk }: { desk: DeskDetail }) {
  const training = useTraining(desk.id);
  if (!training.data) return null;
  const { readiness, autonomy } = training.data;
  return (
    <Section
      icon={<Gauge />}
      title={t('Autonomy')}
      description={t('Questions and corrections people had to give, per piece of work. Lower is better.')}
      actions={
        <Button asChild variant="outline" size="xs">
          <Link to="/w/$workspaceId/desks/$deskId" params={{ workspaceId: desk.workspaceId, deskId: desk.id }} search={{ tab: 'training' }}>
            <GraduationCap /> {t('Training')}
          </Link>
        </Button>
      }
    >
      <AutonomyFigure autonomy={autonomy} />
      {readiness.next && (
        <p className="mt-2 text-xs text-muted-foreground">
          {readiness.ready
            ? t('Ready for {stage}.', { stage: DESK_STAGE_INFO[readiness.next].label })
            : t('{n} things missing for {stage}.', { n: readiness.missing.length, stage: DESK_STAGE_INFO[readiness.next].label })}
        </p>
      )}
    </Section>
  );
}

/** The filters above the Work tab's list, in the order they appear. */
const WORK_FILTERS: readonly { value: string; label: string }[] = [
  { value: 'all', label: t('All') },
  { value: 'open', label: t('Open') },
  ...(['needs_approval', 'working', 'waiting', 'blocked', 'ready', 'created', 'complete', 'failed', 'cancelled'] as const).map((s) => ({
    value: s,
    label: WORK_ITEM_STATUS[s].label,
  })),
];
const WORK_LIMIT = 200;

/** The desk's Work Items — all of them, narrowed by status. */
function DeskWork({ desk }: { desk: DeskDetail }) {
  const toast = useToast();
  const navigate = useNavigate();
  const workspaceId = desk.workspaceId;
  const search = useSearch({ from: '/_authed/w/$workspaceId/desks/$deskId' });
  const filter = WORK_FILTERS.find((f) => f.value === search.status) ?? WORK_FILTERS[0];
  const query = { deskId: desk.id, status: filter.value === 'all' ? undefined : filter.value, limit: WORK_LIMIT };
  const items = useQuery({
    queryKey: qk.workItems(workspaceId, query),
    queryFn: () => api.workItems.list(workspaceId, query),
    placeholderData: keepPreviousData,
  });
  const setFilter = (v: string) => void navigate({ to: '.', search: (s) => ({ ...s, status: v === 'all' ? undefined : v }), replace: true });
  const approveAll = useApiMutation(() => api.desks.approveStarts(desk.id), {
    invalidate: [qk.desk(desk.id), qk.desks(workspaceId), qk.approvalsAll(workspaceId), qk.workItemsAll(workspaceId)],
    onSuccess: (r) => toast.success(r.approved === 1 ? t('1 Work Item approved') : t('{n} Work Items approved', { n: r.approved }), t('They start one after another.')),
  });

  return (
    <Section
      icon={<ListTodo />}
      title={desk.workItemLabel.plural}
      description={
        <>
          {t('Running {running} of {max}', { running: desk.runningWorkItems, max: desk.maxParallelWorkItems })}
          {desk.queuedWorkItems > 0 && <> · {t('{n} waiting in line', { n: desk.queuedWorkItems })}</>}
          {desk.awaitingStartApproval > 0 && <> · {t('{n} waiting for approval', { n: desk.awaitingStartApproval })}</>}
        </>
      }
      actions={
        desk.awaitingStartApproval > 1 && (
          <Button
            variant="outline"
            size="xs"
            onClick={() => approveAll.mutate()}
            loading={approveAll.isPending}
            title={t('Approve every {item} that waits for approval. They still run only as many at a time as the desk allows.', { item: inSentence(desk.workItemLabel.singular) })}
          >
            {!approveAll.isPending && <Check />} {t('Approve all {n}', { n: desk.awaitingStartApproval })}
          </Button>
        )
      }
    >
      <div className="-mt-1 mb-4 flex flex-wrap gap-1" role="group" aria-label={t('Filter by status')}>
        {WORK_FILTERS.map((f) => (
          <Button
            key={f.value}
            variant={f === filter ? 'secondary' : 'ghost'}
            size="xs"
            aria-pressed={f === filter}
            onClick={() => setFilter(f.value)}
            className={cn(f !== filter && 'text-muted-foreground')}
          >
            {f.label}
            {f.value === 'open' && desk.openWorkItems > 0 && <span className="tabular-nums opacity-70">{desk.openWorkItems}</span>}
          </Button>
        ))}
      </div>
      {items.isPending ? (
        <LoadingRows rows={4} />
      ) : items.isError ? (
        <ErrorState error={items.error} onRetry={() => void items.refetch()} />
      ) : items.data.length === 0 ? (
        <EmptyState
          compact
          title={filter.value === 'all' ? t('No work yet') : t('Nothing {status}', { status: inSentence(filter.label) })}
          description={
            filter.value === 'all' || filter.value === 'open'
              ? t('Run the desk so the Lead Agent looks for work — or create a work item yourself.')
              : undefined
          }
        />
      ) : (
        <>
          <SelectableWorkItemList items={items.data} workspaceId={workspaceId} showDesk={false} className="-mx-5 -mb-5 border-t" />
          {items.data.length >= WORK_LIMIT && (
            <div className="-mx-5 mt-5 -mb-5 border-t bg-muted/30 px-4 py-2 text-xs text-muted-foreground">
              {tr('The first {n} — {link}', {
                n: WORK_LIMIT,
                link: (
                  <Link
                    to="/w/$workspaceId/work-items"
                    params={{ workspaceId }}
                    search={{ deskId: desk.id, status: query.status }}
                    className="underline-offset-4 hover:underline"
                  >
                    {t('search all')}
                  </Link>
                ),
              })}
            </div>
          )}
        </>
      )}
    </Section>
  );
}

/** How the desk works and who works on it — set up once, changed now and then. */
function DeskSetup({ desk }: { desk: DeskDetail }) {
  const workspaceId = desk.workspaceId;
  const approvalText = { manual: t('a person approves'), agent: t('an agent approves'), auto: t('starts automatically') }[desk.workItemApproval];
  return (
    <>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="flex min-w-0 flex-col gap-6 lg:col-span-2">
          <Section
            icon={<SlidersHorizontal />}
            title={t('How this desk works')}
            actions={
              <>
                <Button asChild variant="outline" size="xs" title={t('Describe what should change; the setup assistant drafts it for you to apply.')}>
                  <Link to="/w/$workspaceId/start" params={{ workspaceId }} search={{ desk: desk.id }}>
                    <Sparkles /> {t('Change with VICKI')}
                  </Link>
                </Button>
                <EditDeskDialog desk={desk}>
                  <Button variant="outline" size="xs">
                    <Pencil /> {t('Edit')}
                  </Button>
                </EditDeskDialog>
              </>
            }
          >
            <DefinitionList
              items={[
                {
                  label: t('Lead Agent'),
                  value: desk.leadAgent ? (
                    <Link
                      to="/w/$workspaceId/agents/$agentId"
                      params={{ workspaceId, agentId: desk.leadAgent.agentId }}
                      className="hover:text-primary hover:underline"
                    >
                      {desk.leadAgent.name}
                    </Link>
                  ) : (
                    <span className="text-amber-700 dark:text-amber-400">{t('not set')}</span>
                  ),
                },
                { label: t('New work'), value: t('{n} at a time, {approval}', { n: desk.maxParallelWorkItems, approval: approvalText }) },
                {
                  label: t('Stage'),
                  value: (
                    <div className="flex flex-col gap-1">
                      <StageToggle desk={desk} />
                      <TrainingNote desk={desk} />
                    </div>
                  ),
                },
                { label: t('God mode'), value: <AgentModeToggle desk={desk} /> },
                { label: t('Last triggered'), value: <RelativeTime value={desk.lastTriggeredAt} fallback={t('never')} /> },
              ]}
            />
          </Section>
          <DeskPlaybookSection desk={desk} />
          <DeskPlaybooksSection desk={desk} />
          <DeskLeadSection desk={desk} />
          <DeskTriggersSection desk={desk} />
        </div>

        <div className="flex min-w-0 flex-col gap-6">
          <DeskAgentsSection desk={desk} />
          <DeskConnectionsSection desk={desk} />
          <DeskSecretsSection desk={desk} />
          <DeskResourcesSection desk={desk} />
          <DeskPermissions deskId={desk.id} />
        </div>
      </div>

      <TechDetails className="mt-6">
        <div className="grid gap-4">
          <div>
            <div className="mb-1.5 text-xs font-medium text-muted-foreground">{t('Desk ID')}</div>
            <code className="font-mono text-xs">{desk.id}</code>
          </div>
          <div>
            <div className="mb-1.5 text-xs font-medium text-muted-foreground">{t('Current Lead evaluation (execution)')}</div>
            {desk.evaluation ? <JsonBlock value={desk.evaluation} /> : <p className="text-sm text-muted-foreground">{t('No evaluation running.')}</p>}
          </div>
        </div>
      </TechDetails>
    </>
  );
}

/** What happened on the desk, and what it learned. */
function DeskHistory({ desk }: { desk: DeskDetail }) {
  const workspaceId = desk.workspaceId;
  return (
    <>
      <DeskLearningsSection desk={desk} />
      <Section
        icon={<Activity />}
        title={t('Recent activity')}
        actions={
          <Button asChild variant="ghost" size="xs">
            <Link to="/w/$workspaceId/activity" params={{ workspaceId }} search={{ deskId: desk.id }}>
              {t('View all')}
            </Link>
          </Button>
        }
      >
        {desk.recentActivity.length === 0 ? (
          <EmptyState compact title={t('No activity yet')} />
        ) : (
          <ActivityFeed events={desk.recentActivity} workspaceId={workspaceId} compact />
        )}
      </Section>
    </>
  );
}

function LeadEvaluationCard({ desk }: { desk: DeskDetail }) {
  const ev = desk.evaluation;
  const busy = !!ev;
  const [showSession, setShowSession] = useState(false);
  return (
    <Card
      className={cn(
        'overflow-hidden',
        busy && 'border-indigo-200 bg-indigo-50/60 dark:border-indigo-900 dark:bg-indigo-950/30',
      )}
    >
      <div className="flex items-start gap-3 p-4">
        <div
          className={cn(
            'flex size-9 shrink-0 items-center justify-center rounded-full',
            busy ? 'bg-indigo-500/15 text-indigo-600 dark:text-indigo-300' : 'bg-muted text-muted-foreground',
          )}
        >
          {busy ? <BrainCircuit className="size-4.5 animate-soft-pulse" /> : <Bot className="size-4.5" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
            {busy ? t('The Lead Agent is checking the situation') : desk.pendingEvents > 0 ? t('Events are waiting for the Lead Agent') : t('The Lead Agent is resting')}
            {ev && <ExecutionStatusBadge status={ev.status} />}
          </div>
          <p className="mt-0.5 text-[13px] text-muted-foreground">
            {busy ? (
              tr('Started {time} — it decides whether new {items} are needed.', {
                time: <RelativeTime value={ev.startedAt ?? ev.createdAt} />,
                items: inSentence(desk.workItemLabel.plural),
              })
            ) : desk.pendingEvents > 0 ? (
              desk.pendingEvents === 1
                ? t('1 event is waiting to be evaluated. This will happen automatically in a moment.')
                : t('{n} events are waiting to be evaluated. This will happen automatically in a moment.', { n: desk.pendingEvents })
            ) : (
              t('It wakes up as soon as a Trigger fires or you run the desk.')
            )}
          </p>
          {busy && desk.pendingEvents > 0 && (
            <p className="mt-1 text-xs text-muted-foreground">
              {desk.pendingEvents === 1 ? t('After that, 1 more event is waiting.') : t('After that, {n} more events are waiting.', { n: desk.pendingEvents })}
            </p>
          )}
          {ev && <AgentSteps executionId={ev.id} working agentName={t('The Lead Agent')} className="mt-2 bg-card" />}
        </div>
        <Button variant="ghost" size="xs" onClick={() => setShowSession((v) => !v)} title={t('The Lead Agent’s full session for its recent evaluations.')}>
          <ScrollText /> {showSession ? t('Hide session') : t('Agent session')}
        </Button>
      </div>
      {showSession && <LeadSession deskId={desk.id} />}
    </Card>
  );
}

/** The Lead Agent's recent desk evaluations, step by step, as its runtime reported them. */
function LeadSession({ deskId }: { deskId: string }) {
  const transcript = useQuery({ queryKey: qk.deskTranscript(deskId), queryFn: () => api.desks.transcript(deskId) });
  return (
    <div className="border-t bg-card">
      <AgentConversation
        runs={transcript.data}
        isPending={transcript.isPending}
        error={transcript.error}
        onRetry={() => void transcript.refetch()}
        emptyDescription={t('The Lead Agent hasn’t evaluated this desk yet. Its full session shows up here the next time a Trigger fires or you run the desk.')}
      />
    </div>
  );
}

/** Views pinned to this desk, live (docs/features/views.md). Hidden while the desk has none. */
function DeskViews({ desk }: { desk: DeskDetail }) {
  const views = useQuery({ queryKey: viewKeys.list(desk.workspaceId, desk.id), queryFn: () => api.views.list(desk.workspaceId, { deskId: desk.id }) });
  if (!views.data?.length) return null;
  return (
    <Section
      icon={<PanelsTopLeft />}
      title={t('Views')}
      actions={
        <NewViewDialog workspaceId={desk.workspaceId} deskId={desk.id}>
          <Button variant="outline" size="xs">
            <Plus /> {t('New view')}
          </Button>
        </NewViewDialog>
      }
    >
      <div className="grid gap-4">
        {views.data.map((v) => (
          <ViewCard key={v.id} workspaceId={desk.workspaceId} view={v} />
        ))}
      </div>
    </Section>
  );
}
